// The nine process desks and the modules each draws on (BRIEF §1.4, SPEC §17.3, shell-ia §15.1). WP-DESK-HOST.
//
// The agents of each desk are the one table of ECONIC's five agents × the nine processes (MoET, 21 Oct 2026: "we need to do a table match"),
// settled on 22 Oct 2026 by reading each process's stages in PR-01: primary where a main stage rests on the agent, secondary where one stage
// uses it. Its source is PR-01's `agentMatrix` (docs/econic/pmo-evaluation/processes/processes.json); ./drawsOn.test.mjs holds this file,
// the space manifest and docs/econic/pmo-evaluation/process-agent-matrix.md to it. Policy (PMO) is not one of the five: desks 1 and 5 name it
// (secondary) as where a legal instrument's first draft is handed over.
//
// This is the declared matrix, not data: it says which modules a desk uses, never a figure. The desk's own `desk.header` state wins when the
// page publishes one (its `drawsOn` carries live counts); the space manifest's `econic.modules` on a nav entry wins over this table once the
// API carries it (requested from WP-INTEG-QA: SpaceManifest passthrough). Counts and freshness on the chips come from
// `GET /api/econic/modules/status` (the module rail's numbers), so the same number shows here, on the rail and in the module.
import type { DeskKey, DeskNumber, ModuleKey, ReportTypeKey, Sensitivity } from "@ncp/page-sdk/desk";

export interface DeskInfo {
  key: DeskKey;
  n: DeskNumber;
  /** Primary modules first, then secondary (BRIEF §1.4 "P" and "s"). */
  primary: ModuleKey[];
  secondary: ModuleKey[];
  /** The desk's classification floor (SPEC §3.1: desk manifest floor; every ES desk is Internal until its manifest says otherwise). */
  floor: Sensitivity;
  /** The report type behind the header's "Generate PDF" (the unified catalogue, ReportTypes.cs: the type whose `usedFrom` names the desk
   *  as its own deliverable); the header opens WP-PDF-UI's panel for it. */
  report: ReportTypeKey;
}

/** Desk number order (SPEC §4.5). */
export const DESKS: readonly DeskInfo[] = [
  { key: "es-studies", n: 1, primary: ["kb", "outlook"], secondary: ["uae-radar", "global-radar", "country360", "policylab"], floor: "internal", report: "study" },
  { key: "es-international-reports", n: 2, primary: ["global-radar", "country360"], secondary: ["kb", "uae-radar", "outlook"], floor: "internal", report: "international-report-digest" },
  { key: "es-statistical-report", n: 3, primary: ["uae-radar"], secondary: ["kb"], floor: "internal", report: "asr" },
  { key: "es-quarterly-report", n: 4, primary: ["uae-radar", "outlook"], secondary: ["kb", "global-radar"], floor: "internal", report: "qer" },
  { key: "es-publications", n: 5, primary: ["kb"], secondary: ["uae-radar", "policylab"], floor: "internal", report: "publication-pack" },
  { key: "es-open-data", n: 6, primary: ["uae-radar"], secondary: [], floor: "internal", report: "open-data-change-report" },
  { key: "es-scientific-activities", n: 7, primary: ["kb"], secondary: [], floor: "internal", report: "activity-outcomes-report" },
  { key: "es-early-warning", n: 8, primary: ["global-radar", "outlook"], secondary: ["uae-radar", "kb"], floor: "internal", report: "early-warning-bulletin" },
  { key: "es-recovery-exercises", n: 9, primary: ["outlook"], secondary: ["kb", "global-radar"], floor: "internal", report: "after-action-report" },
];

const BY_KEY = new Map<string, DeskInfo>(DESKS.map((d) => [d.key, d]));

/** The desk a page key names, or null for any other page (`data-sources`, `ne-*`, a personal page). */
export function deskOf(pageKey: string | null | undefined): DeskInfo | null {
  return (pageKey && BY_KEY.get(pageKey)) || null;
}

export const deskByNumber = (n: number): DeskInfo | null => DESKS.find((d) => d.n === n) ?? null;

/** Where a module chip leads: the module's home (the Knowledge Bank lives at /knowledge). */
export function moduleHref(module: ModuleKey | "knowledge"): string {
  return module === "kb" || module === "knowledge" ? "/knowledge" : `/m/${module}`;
}

const MODULE_KEYS: readonly ModuleKey[] = ["kb", "uae-radar", "global-radar", "outlook", "country360", "policylab"];
export const isModuleKey = (v: unknown): v is ModuleKey => typeof v === "string" && (MODULE_KEYS as readonly string[]).includes(v);

/**
 * `econic.modules` of a space nav entry (`{ "primary": [...], "secondary": [...] }`), when the API passes it through; else null.
 * Unknown keys are dropped (the Knowledge Bank may be spelled `knowledge`).
 */
export function manifestModules(entry: unknown): { primary: ModuleKey[]; secondary: ModuleKey[] } | null {
  const econic = (entry as { econic?: { modules?: { primary?: unknown; secondary?: unknown } } } | null)?.econic?.modules;
  if (!econic) return null;
  const list = (v: unknown) => (Array.isArray(v) ? v.map((x) => (x === "knowledge" ? "kb" : x)).filter(isModuleKey) : []);
  const primary = list(econic.primary);
  const secondary = list(econic.secondary).filter((m) => !primary.includes(m));
  return primary.length || secondary.length ? { primary, secondary } : null;
}
